// hooks/useCustomerFollowUp.js
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { toast } from "react-toastify";

const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem("cms_token")}`,
  "Content-Type": "application/json",
});

const multipartHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("cms_token")}`,
});

// ── List follow-ups for a customer ───────────────────────────────────────────
export const useCustomerFollowUps = (customerId, filterType = "") => {
  return useQuery({
    queryKey: ["customer-followups", customerId, filterType],
    queryFn: async () => {
      const params = { customer_id: customerId, limit: 50, lead_type: 1 };
      if (filterType && filterType !== "All") {
        params.type = filterType.toLowerCase();
      }
      const res = await axios.get(`${API_BASE_URL}/lead-followups`, {
        headers: headers(),
        params,
      });

      return res.data?.data || [];
    },
    enabled: !!customerId,
    keepPreviousData: true,
  });
};

// ── Create follow-up ─────────────────────────────────────────────────────────
export const useAddCustomerFollowUp = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { attachments, ...rest } = payload;

      const hasFiles =
        Array.isArray(attachments) && attachments.length > 0 && attachments[0] instanceof File;

      if (hasFiles) {
        const fd = new FormData();
        // Add lead_type: 1 to form data
        fd.append("lead_type", 1);
        Object.entries(rest).forEach(([k, v]) => {
          if (v !== "" && v !== null && v !== undefined) {
            fd.append(k, v);
          }
        });
        attachments.forEach((file, idx) => {
          fd.append(`attachments[${idx}]`, file, file.name);
        });

        const res = await axios.post(
          `${API_BASE_URL}/lead-followups`,
          fd,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("cms_token")}`,
            },
          }
        );
        if (res.data?.status === false) throw new Error(res.data.message);
        return { ...res.data, customer_id: rest.customer_id };
      }

      const clean = { lead_type: 1 };
      Object.entries(rest).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) clean[k] = v;
      });
      const res = await axios.post(
        `${API_BASE_URL}/lead-followups`,
        clean,
        { headers: headers() }
      );
      if (res.data?.status === false) throw new Error(res.data.message);
      return { ...res.data, customer_id: clean.customer_id };
    },
    onSuccess: (data, variables) => {
      if (variables?.followup_type !== "email") {
        toast.success("Follow-up saved successfully");
      }
      const cid = data.customer_id;
      qc.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "customer-followups" &&
          String(query.queryKey[1]) === String(cid),
      });
      qc.invalidateQueries({ queryKey: ["lead-details"] });
      qc.invalidateQueries({ queryKey: ["enquiryList"] });
      qc.invalidateQueries({ queryKey: ["inbox-leads"] });
    },
    onError: (err) => {
      toast.error(err?.message || "Failed to save follow-up");
    },
  });
};

// ── Update follow-up ─────────────────────────────────────────────────────────
export const useUpdateCustomerFollowUp = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { id, customer_id, attachments, ...rest } = payload;

      const hasFiles =
        Array.isArray(attachments) && attachments.length > 0 && attachments[0] instanceof File;

      if (hasFiles) {
        const fd = new FormData();
        // Include customer_id and lead_type in the form data
        if (customer_id) fd.append("customer_id", customer_id);
        fd.append("lead_type", 1);
        Object.entries(rest).forEach(([k, v]) => {
          if (v !== "" && v !== null && v !== undefined) {
            fd.append(k, v);
          }
        });
        attachments.forEach((file, idx) => {
          fd.append(`attachments[${idx}]`, file, file.name);
        });

        const res = await axios.put(
          `${API_BASE_URL}/lead-followups/${id}`,
          fd,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("cms_token")}`,
            },
          }
        );
        if (res.data?.status === false) throw new Error(res.data.message);
        return { ...res.data, customer_id };
      }

      // Include customer_id and lead_type in the JSON body
      const clean = { lead_type: 1 };
      if (customer_id) clean.customer_id = customer_id;
      Object.entries(rest).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) clean[k] = v;
      });
      const res = await axios.put(
        `${API_BASE_URL}/lead-followups/${id}`,
        clean,
        { headers: headers() }
      );
      if (res.data?.status === false) throw new Error(res.data.message);
      return { ...res.data, customer_id };
    },
    onSuccess: (data) => {
      toast.success("Follow-up updated");
      const cid = data.customer_id;
      qc.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "customer-followups" &&
          String(query.queryKey[1]) === String(cid),
      });
      qc.invalidateQueries({ queryKey: ["enquiryList"] });
      qc.invalidateQueries({ queryKey: ["inbox-leads"] });
    },
    onError: (err) => {
      toast.error(err?.message || "Failed to update follow-up");
    },
  });
};

// ── Delete follow-up ─────────────────────────────────────────────────────────
export const useDeleteCustomerFollowUp = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, customer_id }) => {
      const res = await axios.delete(
        `${API_BASE_URL}/lead-followups/${id}`,
        { headers: headers() }
      );
      if (res.data?.status === false) throw new Error(res.data.message);
      return { ...res.data, customer_id };
    },
    onSuccess: (data) => {
      toast.success("Follow-up deleted");
      const cid = data.customer_id;
      qc.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "customer-followups" &&
          String(query.queryKey[1]) === String(cid),
      });
      qc.invalidateQueries({ queryKey: ["enquiryList"] });
      qc.invalidateQueries({ queryKey: ["inbox-leads"] });
    },
    onError: (err) => {
      toast.error(err?.message || "Failed to delete follow-up");
    },
  });
};

// ── Update customer stage status ─────────────────────────────────────────────
export const useUpdateCustomerStage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId, follow_up_status, remarks }) => {
      const res = await axios.put(
        `${API_BASE_URL}/leads/${leadId}/stage`,
        { follow_up_status, remarks },
        { headers: headers() }
      );
      if (res.data?.status === false) throw new Error(res.data.message);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Lead stage updated");
      qc.invalidateQueries({ queryKey: ["enquiryList"] });
      qc.invalidateQueries({ queryKey: ["inbox-leads"] });
    },
    onError: (err) => {
      toast.error(err?.message || "Failed to update stage");
    },
  });
};
